/**
 * Thème courant et sa bascule.
 *
 * Le thème est appliqué **avant** le rendu de React, par un script en ligne dans
 * `index.html` : attendre le premier rendu ferait apparaître le portail en clair
 * pendant une fraction de seconde avant de basculer, ce qui est plus désagréable
 * qu'un chargement plus lent.
 *
 * Ce crochet ne fait donc que reprendre l'état déjà posé sur la racine, et le
 * changer sur demande.
 */

import { useCallback, useEffect, useState } from 'react';

import { applyTheme, DARK_CLASS, otherTheme, THEME_STORAGE_KEY, type Theme } from '../lib/theme';

/** Lit le thème effectivement appliqué, plutôt que de le recalculer. */
function themeCourant(): Theme {
  return document.documentElement.classList.contains(DARK_CLASS) ? 'dark' : 'light';
}

export interface ThemeState {
  theme: Theme;
  /** Bascule vers l'autre thème et mémorise le choix. */
  toggle: () => void;
}

export function useTheme(): ThemeState {
  const [theme, setTheme] = useState<Theme>(themeCourant);

  const toggle = useCallback(() => {
    const suivant = otherTheme(themeCourant());
    applyTheme(document.documentElement, suivant);
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, suivant);
    } catch {
      // Navigation privée, stockage saturé ou désactivé : la bascule doit
      // fonctionner pour la session en cours même si le choix n'est pas retenu.
    }
    setTheme(suivant);
  }, []);

  return { theme, toggle };
}

/**
 * Vrai lorsque le thème sombre est actif.
 *
 * Existe pour les graphiques `recharts`, qui reçoivent leurs couleurs en
 * propriétés JavaScript et ne peuvent pas s'appuyer sur des classes CSS. Il
 * observe la classe de la racine plutôt que la préférence du système : depuis la
 * bascule manuelle, les deux peuvent différer, et un graphique aux couleurs de
 * l'autre thème serait illisible.
 */
export function useDarkTheme(): boolean {
  const [dark, setDark] = useState(() => themeCourant() === 'dark');

  useEffect(() => {
    // Observé et non dérivé d'un contexte : la classe peut être changée par le
    // bouton, par un autre onglet, ou par le script d'amorçage. Une seule source
    // de vérité, celle que le CSS lit lui aussi.
    const observer = new MutationObserver(() => setDark(themeCourant() === 'dark'));
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  return dark;
}
